import { useEffect, useRef, useState } from "react";
import { useApiPvpRetrieve } from "@/api/lctApIComponents";
import { PageHeader } from "@/components/PageHeader";
import { useAppSelector } from "@/hooks/reduxHooks";
import { FeedbackSection } from "@/pvp/components/FeedbackSection";
import { PVPAppFormAdminFooter } from "@/pvp/components/PVPAppFormAdminFooter";
import { PVPApplicationForm } from "@/pvp/components/PVPApplicationForm";
import { PVPSectionList } from "@/pvp/components/PVPSectionList";
import { createFileRoute } from "@tanstack/react-router";
import { useDimensions } from "@/hooks/useDimensions";
import { FeedbackButton } from "@/pvp/components/FeedbackSection/FeedbackButton";
import { useFeedback } from "@/hooks/useFeedback";
import { useAuth } from "@/auth/hooks/useAuth";

type ApplicationSearch = {
  userAppId: string;
  appId: string;
  email: string;
};

export const Route = createFileRoute("/_auth/admin/pvp/$pvpId")({
  validateSearch: (search: Record<string, unknown>): ApplicationSearch => {
    return {
      userAppId: (search.userAppId as string) || "",
      appId: (search.appId as string) || "",
      email: (search.email as string) || "",
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { pvpId } = Route.useParams();
  const { userAppId, appId } = Route.useSearch();
  const token = useAppSelector((state) => state.user.authKey);
  const containerRef = useRef(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const { isSmallScreen } = useDimensions();
  const { isStaff } = useAuth();
  const { feedbackItemsLength } = useFeedback(userAppId);

  const { data } = useApiPvpRetrieve({
    headers: { Authorization: `Token ${token}` },
    pathParams: { id: pvpId },
  });

  const toggleFeedback = () => {
    setShowFeedback(!showFeedback);
  };
  const hasFeedback = feedbackItemsLength > 0;
  const showFeedbackSection = hasFeedback || isStaff;

  const hideSectionList = showFeedback && isSmallScreen;

  useEffect(() => {
    if (hasFeedback) {
      setShowFeedback(true);
    }
  }, [hasFeedback]);

  return (
    <div className="min grid h-screen grid-cols-[1fr] grid-rows-[auto_auto_1fr_auto] overflow-hidden bg-gray-100 md:grid-cols-[auto_1fr_auto] md:grid-rows-[auto_1fr_auto]">
      <div className="col-span-full">
        <PageHeader title={data?.title} />
      </div>
      <div className="col-span-1 flex flex-wrap items-center gap-x-4 overflow-auto p-4 md:m-6 md:items-start">
        {hideSectionList ? null : <PVPSectionList appId={appId} pvpId={pvpId} userAppId={userAppId} />}
        {!showFeedback && isSmallScreen && showFeedbackSection ? (
          <FeedbackButton onClick={toggleFeedback} hasFeedback={hasFeedback} />
        ) : null}
      </div>
      {showFeedback && isSmallScreen ? null : (
        <div
          className="no-scrollbar col-span-1 min-h-0 min-w-[300px] max-w-[800px] overflow-y-auto px-4 md:p-8 md:pl-2 md:pr-4"
          ref={containerRef}
        >
          <PVPApplicationForm pvpId={pvpId} appId={appId} userAppId={userAppId} />
        </div>
      )}
      <div className={"col-span-1 min-h-0 px-4 md:pb-8 md:pt-6"}>
        {showFeedbackSection && showFeedback ? (
          <FeedbackSection pvpId={pvpId} appId={appId} userAppId={userAppId} toggleFeedback={toggleFeedback} />
        ) : null}
        {showFeedbackSection && !showFeedback && !isSmallScreen ? (
          <FeedbackButton onClick={toggleFeedback} hasFeedback={hasFeedback} />
        ) : null}
      </div>
      <div className="col-span-full">
        <PVPAppFormAdminFooter userAppId={userAppId} />
      </div>
    </div>
  );
}
